import axios from 'axios';
import {
  contaAtualId,
  contaLocalStorage,
  garantirMigracaoDeArmazenamento,
} from 'dashboard/helper/accountScopedStorage';

const CHAVE_URL = 'kanban_webhook_url';
const CHAVE_ATIVO = 'kanban_webhook_enabled';
const CHAVE_HISTORICO = 'kanban_webhook_event_history';

// O servico e um singleton de modulo, entao ele guarda de que conta a
// configuracao carregada veio: a URL de webhook de uma conta mandando cartao de
// outra era o pior caso desta auditoria.
class WebhookService {
  constructor() {
    this.contaCarregada = null;
    this.urlAtual = '';
    this.ativoAtual = false;
  }

  // Getter, e nao campo: todo envio rele a conta antes de disparar. Um campo
  // preenchido no import ficaria preso na conta em que o modulo foi avaliado.
  get webhookUrl() {
    this.carregarConfig();
    return this.urlAtual;
  }

  get isEnabled() {
    this.carregarConfig();
    return this.ativoAtual;
  }

  carregarConfig() {
    garantirMigracaoDeArmazenamento();
    const conta = contaAtualId();
    if (!conta) {
      this.contaCarregada = null;
      this.urlAtual = '';
      this.ativoAtual = false;
      return;
    }
    if (this.contaCarregada === conta) return;
    this.contaCarregada = conta;
    this.urlAtual = contaLocalStorage.getItem(CHAVE_URL) || '';
    this.ativoAtual = contaLocalStorage.getItem(CHAVE_ATIVO) === 'true';
  }

  setWebhookConfig({ url, enabled }) {
    this.carregarConfig();
    // Sem conta no contexto nao grava, e sem gravar nao dispara.
    if (!contaAtualId()) return;
    this.urlAtual = url;
    this.ativoAtual = Boolean(enabled);
    contaLocalStorage.setItem(CHAVE_URL, url);
    contaLocalStorage.setItem(CHAVE_ATIVO, String(Boolean(enabled)));
  }

  formatItemData(itemData) {
    if (!itemData) {
      console.warn('WebhookService - itemData é null ou undefined');
      return null;
    }

    // Retorna o objeto completo sem formatação adicional
    // para manter todos os dados originais do item
    return {
      id: itemData.id,
      account_id: itemData.account_id,
      conversation_display_id: itemData.conversation_display_id,
      funnel_id: itemData.funnel_id,
      funnel_stage: itemData.funnel_stage,
      stage_entered_at: itemData.stage_entered_at,
      position: itemData.position,
      custom_attributes: itemData.custom_attributes || {},
      item_details: {
        ...itemData.item_details,
        notes: itemData.item_details?.notes || [],
        checklist: itemData.item_details?.checklist || [],
        title: itemData.item_details?.title,
        value: itemData.item_details?.value,
        agent_id: itemData.item_details?.agent_id,
        priority: itemData.item_details?.priority,
        description: itemData.item_details?.description,
        conversation_id: itemData.item_details?.conversation_id,
      },
      timer_started_at: itemData.timer_started_at,
      timer_duration: itemData.timer_duration,
      created_at: itemData.created_at,
      updated_at: itemData.updated_at,
    };
  }

  async notifyStageChange(itemData, fromStage, toStage) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.stage_changed',
        data: {
          item: formattedItem,
          from_stage: fromStage,
          to_stage: toStage,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook:', error);
      // Não lança erro!
    }
  }

  async notifyItemUpdated(itemData, changes = {}) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.updated',
        data: {
          item: formattedItem,
          changes: changes,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de atualização:', error);
      // Não lança erro!
    }
  }

  async notifyItemCreated(itemData) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.created',
        data: {
          item: formattedItem,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de criação:', error);
      // Não lança erro!
    }
  }

  async notifyItemDeleted(itemData) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.deleted',
        data: {
          item: formattedItem,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de exclusão:', error);
      // Não lança erro!
    }
  }

  async notifyTimerStarted(itemData) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.timer_started',
        data: {
          item: formattedItem,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de início do timer:', error);
      // Não lança erro!
    }
  }

  async notifyTimerStopped(itemData, duration) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItem = this.formatItemData(itemData);

      const payload = {
        event: 'kanban.item.timer_stopped',
        data: {
          item: formattedItem,
          duration: duration,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de parada do timer:', error);
      // Não lança erro!
    }
  }

  async notifyItemReordered(items, changes) {
    if (!this.isEnabled || !this.webhookUrl) {
      console.log('WebhookService - Webhook desativado ou sem URL configurada');
      return;
    }

    try {
      const formattedItems = items.map(item => this.formatItemData(item));

      const payload = {
        event: 'kanban.items.reordered',
        data: {
          items: formattedItems,
          changes: changes,
          timestamp: new Date().toISOString(),
        },
      };

      await axios.post(this.webhookUrl, payload);
    } catch (error) {
      console.error('Erro ao notificar webhook de reordenação:', error);
      // Não lança erro!
    }
  }

  async testWebhookConnection() {
    if (!this.webhookUrl) {
      return { success: false, message: 'Webhook URL não configurada.' };
    }
    try {
      const payload = {
        event: 'kanban.webhook.test',
        data: {
          message: 'Teste de conexão do webhook',
          timestamp: new Date().toISOString(),
        },
      };
      const response = await axios.post(this.webhookUrl, payload);
      this._addEventHistory({
        event: 'test',
        payload,
        status: 'success',
        response: response.data,
        timestamp: new Date().toISOString(),
      });
      return {
        success: true,
        message: 'Conexão bem-sucedida!',
        response: response.data,
      };
    } catch (error) {
      this._addEventHistory({
        event: 'test',
        payload: {
          event: 'kanban.webhook.test',
        },
        status: 'error',
        error: error?.message || error,
        timestamp: new Date().toISOString(),
      });
      return {
        success: false,
        message: 'Falha na conexão.',
        error: error?.message || error,
      };
    }
  }

  getEventHistory() {
    this.carregarConfig();
    const history = contaLocalStorage.getJSON(CHAVE_HISTORICO, []);
    return Array.isArray(history) ? history : [];
  }

  _addEventHistory(entry) {
    const history = this.getEventHistory();
    history.unshift(entry);
    if (history.length > 20) history.length = 20;
    contaLocalStorage.setJSON(CHAVE_HISTORICO, history);
  }
}

export default new WebhookService();
